import React, { useState, useEffect } from 'react';
import { 
  Home, 
  FileText, 
  User, 
  Settings, 
  HelpCircle, 
  Search,
  MessageCircle,
  Briefcase,
  Menu,
  X,
  Shield,
  BarChart3,
  Users,
  CreditCard,
  LogOut,
  Crown,
  GraduationCap,
  UserCheck,
  FileSignature
} from 'lucide-react';
import { useAuth } from '../auth/AuthComponents';
import { useUsage } from '../../contexts/UsageContext';
import { ThemeToggle } from '../../contexts/ThemeContext';
import { publicPlanLabel, getUsageDisplayValues } from '../../utils/planLabels';
import { canUseHiringShortlist, canBrowseFindLawyer } from '../../utils/userPermissions';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../shared/NotificationBell';
import DocxsAiLogo from '../shared/DocxsAiLogo';

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { usage, subscription, fetchUsage } = useUsage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchUsage();
    }
  }, [user, fetchUsage]);

  const navigation = [
    { name: 'Dashboard', icon: Home, path: '/dashboard', current: window.location.pathname === '/dashboard' },
    { name: 'Document Analysis', icon: FileText, path: '/document-analysis', current: window.location.pathname === '/document-analysis' },
    ...(canUseHiringShortlist(user?.role)
      ? [{ name: 'Hiring Shortlist', icon: UserCheck, path: '/hiring-shortlist', current: window.location.pathname === '/hiring-shortlist' }]
      : []),
    { name: 'eSign', icon: FileSignature, path: '/esign', current: window.location.pathname.startsWith('/esign') },
    { name: 'Revamp Document', icon: BarChart3, path: '/revamp-document', current: window.location.pathname === '/revamp-document' },
    { name: 'Generated Documents', icon: FileText, path: '/generated-documents', current: window.location.pathname === '/generated-documents' },
    ...(canBrowseFindLawyer(user?.role)
      ? [{ name: 'Find a Lawyer', icon: Users, path: '/find-a-lawyer', current: window.location.pathname.startsWith('/find-a-lawyer') || window.location.pathname.startsWith('/lawyers/') }]
      : []),
    { name: 'Job Board', icon: Briefcase, path: '/jobs', current: window.location.pathname === '/jobs' || window.location.pathname === '/my-job-match' },
    { name: 'Messages', icon: MessageCircle, path: '/messages', current: window.location.pathname.startsWith('/messages') },
    { name: 'SOP Opportunities', icon: GraduationCap, path: '/sop-opportunities', current: window.location.pathname === '/sop-opportunities' },
    { name: 'Profile', icon: User, path: '/profile', current: window.location.pathname === '/profile' },
    { name: 'Subscription', icon: Crown, path: '/subscriptions', current: window.location.pathname === '/subscriptions' },
    ...(isAdmin ? [{ name: 'Admin', icon: Shield, path: '/admin', current: window.location.pathname === '/admin' }] : []),
  ];

  const secondaryNavigation = [
    { name: 'Settings', icon: Settings, path: '/settings', current: window.location.pathname === '/settings' },
    { name: 'Help', icon: HelpCircle, path: '/help', current: window.location.pathname === '/help' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  // Helper to get current plan name
  const getCurrentPlanName = () => {
    const slug =
      usage?.subscription_plan ||
      subscription?.subscription_plan ||
      user?.subscription_plan ||
      'free';
    return publicPlanLabel(slug);
  };

  const { used: usageUsed, limit: usageLimit } = getUsageDisplayValues(usage);

  return (
    <div className="h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex overflow-hidden">
      {/* Sidebar */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 flex h-full w-64 flex-col bg-[var(--color-surface-muted)] border-r border-[var(--color-border)] transition-transform duration-300 ease-in-out lg:static lg:inset-0 lg:h-full lg:min-h-0 lg:shrink-0 lg:translate-x-0`}>
        
        {/* Sidebar header */}
        <div className="flex min-h-0 shrink-0 items-center justify-between gap-2 bg-[var(--color-bg)] px-3 py-1 border-b border-[var(--color-border)]">
          <div className="min-w-0 flex-1 flex items-center">
            <DocxsAiLogo className="h-16 w-full max-w-full object-contain object-left" />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User profile section */}
        <div className="shrink-0 border-b border-[var(--color-border)] px-4 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] rounded-lg flex items-center justify-center text-[var(--color-text-inverse)] font-semibold">
              {user?.full_name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--color-text)] truncate">
                {user?.full_name}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">
                {getCurrentPlanName()}
              </p>
            </div>
            <button 
              onClick={() => {
                fetchUsage();
              }}
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] cursor-pointer ml-2"
              title="Refresh usage data"
            >
              🔄
            </button>
          </div>
          
          {/* Usage bar */}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-[var(--color-text-muted)] mb-1">
              <span>Usage</span>
              <span>
                {usageUsed}/{usageLimit}
              </span>
            </div>
            <div className="w-full bg-[var(--color-border)] rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${Math.min(((usageUsed / (usageLimit || 1)) * 100), 100)}%` 
                }}
              />
            </div>
          </div>
        </div>

        {/* Scrollable navigation */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <nav className="space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.path)}
                className={`${
                  item.current
                    ? 'bg-[var(--color-nav-active)] text-[var(--color-text)] border-r-2 border-[var(--color-primary)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]'
                } group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium transition-colors`}
              >
                <item.icon
                  className={`${
                    item.current ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)] group-hover:text-[var(--color-text-secondary)]'
                  } mr-3 h-5 w-5 shrink-0`}
                />
                {item.name}
              </button>
            ))}
          </nav>

          <div className="border-t border-[var(--color-border)] px-2 py-4">
            {secondaryNavigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.path)}
                className={`${
                  item.current
                    ? 'bg-[var(--color-nav-active)] text-[var(--color-text)] border-r-2 border-[var(--color-primary)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]'
                } group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium transition-colors`}
              >
                <item.icon className={`${
                  item.current ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)] group-hover:text-[var(--color-text-secondary)]'
                } mr-3 h-5 w-5 shrink-0`} />
                {item.name}
              </button>
            ))}

            <button
              onClick={logout}
              className="group mt-2 flex w-full items-center rounded-md px-3 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-red-600 hover:text-white"
            >
              <LogOut className="mr-3 h-5 w-5 shrink-0 text-[var(--color-text-muted)] group-hover:text-white" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          style={{ backgroundColor: 'var(--color-overlay)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navigation */}
        <header className="bg-[var(--color-surface)] border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between h-16 px-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
              >
                <Menu className="h-5 w-5" />
              </button>
              
              {/* Search */}
              <div className="hidden md:block">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-[var(--color-text-muted)]" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-[var(--color-border)] rounded-md leading-5 bg-[var(--color-input-bg)] text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-ring)] sm:text-sm"
                    placeholder="Search..."
                  />
                </div>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-3">
              <ThemeToggle compact />
              <NotificationBell />
              <button
                onClick={() => navigate('/')}
                className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)] px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Home
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-[var(--color-bg)]">
          <div className="h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout; 